import React, { useState, useCallback } from "react";
import { Button, DatePicker, Input, Select, Table, Tag, Tabs } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getInvoiceReport, getCollectionsReport, getOutstandingReport } from "../../apis/reports";
import CommonPagination from "../../components/CommanPagination";

const { RangePicker } = DatePicker;
const { Option } = Select;

const fmt = (n: any) => `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = dayjs().format("YYYY-MM-DD");
const monthStart = dayjs().startOf("month").format("YYYY-MM-DD");

const SOURCE_LABELS: Record<string, string> = {
  visit: "OPD/IPD",
  pharmacy: "Pharmacy",
  pathology: "Lab",
};

const MODE_LABELS: Record<string, string> = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  cheque: "Cheque",
  online: "Online",
};

// ─── Tally XML helpers ───────────────────────────────────────────────────────

function tallyDate(iso: string | null | undefined) {
  if (!iso) return dayjs().format("YYYYMMDD");
  return iso.replace(/-/g, "");
}

function escXml(s: any) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function buildSalesVoucherXml(invoices: any[]): string {
  const vouchers = invoices.map((inv) => `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER VCHTYPE="Sales" ACTION="Create" OBJVIEW="Invoice Voucher View">
        <DATE>${tallyDate(inv.invoiceDate)}</DATE>
        <VOUCHERNUMBER>${escXml(inv.invoiceNumber)}</VOUCHERNUMBER>
        <REFERENCE>${escXml(inv.invoiceNumber)}</REFERENCE>
        <NARRATION>${escXml(SOURCE_LABELS[inv.sourceType] ?? inv.sourceType)} - ${escXml(inv.patientName)}</NARRATION>
        <PARTYLEDGERNAME>${escXml(inv.patientName || "Cash Patient")}</PARTYLEDGERNAME>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${escXml(inv.patientName || "Cash Patient")}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${Number(inv.totalAmount).toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${escXml(SOURCE_LABELS[inv.sourceType] ?? "Medical Income")}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${Number(inv.totalAmount).toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
      </VOUCHER>
    </TALLYMESSAGE>`).join("\n");

  return wrapTallyEnvelope(vouchers);
}

function buildReceiptVoucherXml(collections: any[]): string {
  const vouchers = collections.map((txn) => `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER VCHTYPE="Receipt" ACTION="Create">
        <DATE>${tallyDate(txn.paymentDate)}</DATE>
        <VOUCHERNUMBER>${escXml(txn.receiptNumber)}</VOUCHERNUMBER>
        <NARRATION>${escXml(txn.notes || "")}</NARRATION>
        <PARTYLEDGERNAME>${escXml(txn.patientName || "Cash Patient")}</PARTYLEDGERNAME>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${escXml(MODE_LABELS[txn.paymentMode] || txn.paymentMode || "Cash")}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${Number(txn.amount).toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${escXml(txn.patientName || "Cash Patient")}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${Number(txn.amount).toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
      </VOUCHER>
    </TALLYMESSAGE>`).join("\n");

  return wrapTallyEnvelope(vouchers);
}

function wrapTallyEnvelope(inner: string) {
  return `<?xml version="1.0" encoding="UTF-8"?>
<ENVELOPE>
  <HEADER>
    <TALLYREQUEST>Import Data</TALLYREQUEST>
  </HEADER>
  <BODY>
    <IMPORTDATA>
      <REQUESTDESC>
        <REPORTNAME>Vouchers</REPORTNAME>
      </REQUESTDESC>
      <REQUESTDATA>
        ${inner}
      </REQUESTDATA>
    </IMPORTDATA>
  </BODY>
</ENVELOPE>`;
}

function downloadXml(xml: string, filename: string) {
  const blob = new Blob([xml], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(rows: any[], columns: string[], filename: string) {
  const lines = [columns.join(","), ...rows.map((r) => columns.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Invoice Report ───────────────────────────────────────────────────────────

const InvoiceReport: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[string, string]>([monthStart, today]);
  const [sourceType, setSourceType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetch = useCallback((overrides: any = {}) => {
    setLoading(true);
    getInvoiceReport({
      date_from: dateRange[0],
      date_to: dateRange[1],
      source_type: sourceType || undefined,
      status: status || undefined,
      search: search || undefined,
      page: overrides.page ?? page,
      per_page: 50,
    }).then((res: any) => {
      setData(res.data?.data || []);
      setSummary(res.data?.summary);
      setMeta(res.data?.meta);
    }).catch(console.error).finally(() => setLoading(false));
  }, [dateRange, sourceType, status, search, page]);

  const columns: ColumnsType<any> = [
    { title: "Invoice #", dataIndex: "invoiceNumber", key: "invoiceNumber", width: 140, render: (v) => <span className="font-mono text-xs">{v || "—"}</span> },
    { title: "Date", dataIndex: "invoiceDate", key: "invoiceDate", width: 110 },
    { title: "Patient", dataIndex: "patientName", key: "patientName", render: (v, r) => <div><div className="font-medium text-sm">{v || "—"}</div><div className="text-xs text-gray-400">{r.patientPhone}</div></div> },
    { title: "Type", dataIndex: "sourceType", key: "sourceType", width: 100, render: (v) => <Tag color="blue">{SOURCE_LABELS[v] ?? v}</Tag> },
    { title: "Total", dataIndex: "totalAmount", key: "totalAmount", align: "right" as const, width: 110, render: (v) => <span className="tabular-nums">{fmt(v)}</span> },
    { title: "Paid", dataIndex: "paidAmount", key: "paidAmount", align: "right" as const, width: 110, render: (v) => <span className="tabular-nums text-green-700">{fmt(v)}</span> },
    { title: "Balance", dataIndex: "balance", key: "balance", align: "right" as const, width: 110, render: (v) => <span className={`tabular-nums font-medium ${Number(v) > 0 ? "text-red-600" : "text-gray-500"}`}>{fmt(v)}</span> },
    { title: "Status", dataIndex: "status", key: "status", width: 90, render: (v) => <Tag color={v === "paid" ? "green" : v === "partial" ? "orange" : "red"}>{v}</Tag> },
  ];

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <RangePicker
          defaultValue={[dayjs(monthStart), dayjs(today)]}
          onChange={(_, s) => setDateRange([s[0], s[1]])}
          format="YYYY-MM-DD"
          size="small"
        />
        <Select placeholder="Type" value={sourceType || undefined} onChange={(v) => setSourceType(v ?? "")} allowClear size="small" style={{ width: 130 }}>
          <Option value="visit">OPD/IPD</Option>
          <Option value="pharmacy">Pharmacy</Option>
          <Option value="pathology">Lab</Option>
        </Select>
        <Select placeholder="Status" value={status || undefined} onChange={(v) => setStatus(v ?? "")} allowClear size="small" style={{ width: 120 }}>
          <Option value="paid">Paid</Option>
          <Option value="partial">Partial</Option>
          <Option value="unpaid">Unpaid</Option>
        </Select>
        <Input
          placeholder="Search patient / invoice..."
          prefix={<SearchOutlined className="text-gray-400" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          size="small"
          style={{ width: 200 }}
        />
        <Button type="primary" size="small" onClick={() => { setPage(1); fetch({ page: 1 }); }}>
          Run
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv(data, ["invoiceNumber", "invoiceDate", "patientName", "patientPhone", "sourceType", "totalAmount", "paidAmount", "balance", "status"], `invoices-${dateRange[0]}-${dateRange[1]}.csv`)}>CSV</Button>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadXml(buildSalesVoucherXml(data), `tally-invoices-${dateRange[0]}-${dateRange[1]}.xml`)}>Tally XML</Button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <SummaryCard label="Total Invoiced" value={fmt(summary.totalInvoiced)} color="blue" />
          <SummaryCard label="Total Collected" value={fmt(summary.totalCollected)} color="green" />
          <SummaryCard label="Outstanding" value={fmt(Number(summary.totalInvoiced ?? 0) - Number(summary.totalCollected ?? 0))} color="red" />
        </div>
      )}

      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={columns} pagination={false} locale={{ emptyText: "No invoices. Apply filters and click Run." }} />
      <CommonPagination meta={meta} onChange={(p) => { setPage(p); fetch({ page: p }); }} />
    </div>
  );
};

// ─── Collections Report ───────────────────────────────────────────────────────

const CollectionsReport: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[string, string]>([monthStart, today]);
  const [paymentMode, setPaymentMode] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetch = useCallback((overrides: any = {}) => {
    setLoading(true);
    getCollectionsReport({
      date_from: dateRange[0],
      date_to: dateRange[1],
      payment_mode: paymentMode || undefined,
      search: search || undefined,
      page: overrides.page ?? page,
      per_page: 50,
    }).then((res: any) => {
      setData(res.data?.data || []);
      setSummary(res.data?.summary);
      setMeta(res.data?.meta);
    }).catch(console.error).finally(() => setLoading(false));
  }, [dateRange, paymentMode, search, page]);

  const columns: ColumnsType<any> = [
    { title: "Receipt #", dataIndex: "receiptNumber", key: "receiptNumber", width: 130, render: (v) => <span className="font-mono text-xs">{v || "—"}</span> },
    { title: "Date", dataIndex: "paymentDate", key: "paymentDate", width: 110 },
    { title: "Patient", dataIndex: "patientName", key: "patientName", render: (v, r) => <div><div className="font-medium text-sm">{v || "—"}</div><div className="text-xs text-gray-400">{r.patientPhone}</div></div> },
    { title: "Mode", dataIndex: "paymentMode", key: "paymentMode", width: 100, render: (v) => <Tag color="purple">{MODE_LABELS[v] ?? v}</Tag> },
    { title: "Amount", dataIndex: "amount", key: "amount", align: "right" as const, width: 120, render: (v) => <span className="tabular-nums font-semibold text-green-700">{fmt(v)}</span> },
    { title: "Notes", dataIndex: "notes", key: "notes", render: (v) => <span className="text-xs text-gray-500">{v || "—"}</span> },
  ];

  const byMode = summary?.byMode ?? {};

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <RangePicker
          defaultValue={[dayjs(monthStart), dayjs(today)]}
          onChange={(_, s) => setDateRange([s[0], s[1]])}
          format="YYYY-MM-DD"
          size="small"
        />
        <Select placeholder="Payment Mode" value={paymentMode || undefined} onChange={(v) => setPaymentMode(v ?? "")} allowClear size="small" style={{ width: 140 }}>
          <Option value="cash">Cash</Option>
          <Option value="upi">UPI</Option>
          <Option value="card">Card</Option>
          <Option value="cheque">Cheque</Option>
          <Option value="online">Online</Option>
        </Select>
        <Input
          placeholder="Search patient / receipt..."
          prefix={<SearchOutlined className="text-gray-400" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          size="small"
          style={{ width: 200 }}
        />
        <Button type="primary" size="small" onClick={() => { setPage(1); fetch({ page: 1 }); }}>
          Run
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv(data, ["receiptNumber", "paymentDate", "patientName", "patientPhone", "paymentMode", "amount", "notes"], `collections-${dateRange[0]}-${dateRange[1]}.csv`)}>CSV</Button>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadXml(buildReceiptVoucherXml(data), `tally-receipts-${dateRange[0]}-${dateRange[1]}.xml`)}>Tally XML</Button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SummaryCard label="Total Collected" value={fmt(summary.totalCollected)} color="green" span={2} />
            {Object.entries(byMode).map(([mode, m]: any) => (
              <SummaryCard key={mode} label={MODE_LABELS[mode] ?? mode} value={`${fmt(m.total)} (${m.count})`} color="blue" />
            ))}
          </div>
        </div>
      )}

      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={columns} pagination={false} locale={{ emptyText: "No collections. Apply filters and click Run." }} />
      <CommonPagination meta={meta} onChange={(p) => { setPage(p); fetch({ page: p }); }} />
    </div>
  );
};

// ─── Outstanding Dues ─────────────────────────────────────────────────────────

const OutstandingReport: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[string, string]>(["", ""]);
  const [sourceType, setSourceType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetch = useCallback((overrides: any = {}) => {
    setLoading(true);
    getOutstandingReport({
      date_from: dateRange[0] || undefined,
      date_to: dateRange[1] || undefined,
      source_type: sourceType || undefined,
      search: search || undefined,
      page: overrides.page ?? page,
      per_page: 50,
    }).then((res: any) => {
      setData(res.data?.data || []);
      setSummary(res.data?.summary);
      setMeta(res.data?.meta);
    }).catch(console.error).finally(() => setLoading(false));
  }, [dateRange, sourceType, search, page]);

  const columns: ColumnsType<any> = [
    { title: "Invoice #", dataIndex: "invoiceNumber", key: "invoiceNumber", width: 140, render: (v) => <span className="font-mono text-xs">{v || "—"}</span> },
    { title: "Date", dataIndex: "invoiceDate", key: "invoiceDate", width: 110 },
    { title: "Patient", dataIndex: "patientName", key: "patientName", render: (v, r) => <div><div className="font-medium text-sm">{v || "—"}</div><div className="text-xs text-gray-400">{r.patientPhone}</div></div> },
    { title: "Type", dataIndex: "sourceType", key: "sourceType", width: 100, render: (v) => <Tag color="blue">{SOURCE_LABELS[v] ?? v}</Tag> },
    { title: "Invoice Total", dataIndex: "totalAmount", key: "totalAmount", align: "right" as const, width: 120, render: (v) => <span className="tabular-nums">{fmt(v)}</span> },
    { title: "Paid", dataIndex: "paidAmount", key: "paidAmount", align: "right" as const, width: 110, render: (v) => <span className="tabular-nums text-green-700">{fmt(v)}</span> },
    { title: "Outstanding", dataIndex: "balance", key: "balance", align: "right" as const, width: 120, render: (v) => <span className="tabular-nums font-bold text-red-600">{fmt(v)}</span> },
    { title: "Status", dataIndex: "status", key: "status", width: 90, render: (v) => <Tag color={v === "partial" ? "orange" : "red"}>{v}</Tag> },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <RangePicker
          onChange={(_, s) => setDateRange([s[0], s[1]])}
          format="YYYY-MM-DD"
          size="small"
          placeholder={["Invoice from", "Invoice to"]}
        />
        <Select placeholder="Type" value={sourceType || undefined} onChange={(v) => setSourceType(v ?? "")} allowClear size="small" style={{ width: 130 }}>
          <Option value="visit">OPD/IPD</Option>
          <Option value="pharmacy">Pharmacy</Option>
          <Option value="pathology">Lab</Option>
        </Select>
        <Input
          placeholder="Search patient / invoice..."
          prefix={<SearchOutlined className="text-gray-400" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          size="small"
          style={{ width: 200 }}
        />
        <Button type="primary" size="small" onClick={() => { setPage(1); fetch({ page: 1 }); }}>
          Run
        </Button>
        <div className="ml-auto">
          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv(data, ["invoiceNumber", "invoiceDate", "patientName", "patientPhone", "sourceType", "totalAmount", "paidAmount", "balance", "status"], `outstanding-dues.csv`)}>CSV</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-1 gap-3 mb-4" style={{ maxWidth: 220 }}>
          <SummaryCard label="Total Outstanding" value={fmt(summary.totalOutstanding)} color="red" />
        </div>
      )}

      <Table rowKey="id" size="small" loading={loading} dataSource={data} columns={columns} pagination={false} locale={{ emptyText: "No outstanding dues. Click Run to load." }} />
      <CommonPagination meta={meta} onChange={(p) => { setPage(p); fetch({ page: p }); }} />
    </div>
  );
};

// ─── Summary card ─────────────────────────────────────────────────────────────

const colorMap: Record<string, string> = {
  blue: "bg-blue-50 border-blue-200 text-blue-800",
  green: "bg-green-50 border-green-200 text-green-800",
  red: "bg-red-50 border-red-200 text-red-800",
  purple: "bg-purple-50 border-purple-200 text-purple-800",
};

const SummaryCard: React.FC<{ label: string; value: string; color?: string; span?: number }> = ({ label, value, color = "blue", span }) => (
  <div className={`rounded-lg border px-4 py-3 ${colorMap[color] ?? colorMap.blue}`} style={span ? { gridColumn: `span ${span}` } : undefined}>
    <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-lg font-bold mt-0.5">{value}</div>
  </div>
);

// ─── Tally Export Guide ────────────────────────────────────────────────────────

const TallyGuide: React.FC = () => (
  <div className="max-w-2xl space-y-4 text-sm text-gray-700">
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
      <h3 className="font-semibold text-amber-900 mb-2">How to import into Tally Prime / Tally ERP 9</h3>
      <ol className="list-decimal list-inside space-y-1.5 text-amber-800">
        <li>Go to the <strong>Invoice Report</strong> or <strong>Collections Report</strong> tab</li>
        <li>Select a date range and click <strong>Run</strong> to load data</li>
        <li>Click <strong>Tally XML</strong> to download the XML file</li>
        <li>In Tally, go to <strong>Gateway of Tally → Import → Data</strong></li>
        <li>Select the downloaded <code>.xml</code> file and import</li>
      </ol>
    </div>

    <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
      <h3 className="font-semibold text-blue-900 mb-2">What gets exported</h3>
      <ul className="list-disc list-inside space-y-1 text-blue-800">
        <li><strong>Invoice XML</strong> — Creates <em>Sales Vouchers</em> in Tally (one per invoice)</li>
        <li><strong>Collections XML</strong> — Creates <em>Receipt Vouchers</em> in Tally (one per payment)</li>
        <li>Ledger names used: Patient name (debtor), OPD/IPD / Pharmacy / Lab (income), Cash / UPI / Card etc. (bank/cash)</li>
      </ul>
    </div>

    <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-900 mb-2">Before importing</h3>
      <ul className="list-disc list-inside space-y-1 text-gray-600">
        <li>Ensure ledger names in Tally match: <em>OPD/IPD</em>, <em>Pharmacy</em>, <em>Lab</em>, <em>Cash</em>, <em>UPI</em>, <em>Card</em></li>
        <li>Create a ledger for each patient name under Sundry Debtors, or use a single <em>Patients Receivable</em> ledger</li>
        <li>Always import in a test company first before importing into production</li>
      </ul>
    </div>
  </div>
);

// ─── Main Reports component ───────────────────────────────────────────────────

const Reports: React.FC = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4">
      <div className="mb-4">
        <h1 className="text-base font-semibold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">Select a date range, apply filters, and click Run to generate a report. Export to CSV or Tally XML.</p>
      </div>

      <Tabs
        defaultActiveKey="invoices"
        items={[
          { key: "invoices", label: "Invoice Report", children: <InvoiceReport /> },
          { key: "collections", label: "Collections", children: <CollectionsReport /> },
          { key: "outstanding", label: "Outstanding Dues", children: <OutstandingReport /> },
          { key: "tally", label: "Tally Export Guide", children: <TallyGuide /> },
        ]}
      />
    </div>
  );
};

export default Reports;
